import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  FileText, 
  ExternalLink, 
  Upload, 
  CheckCircle, 
  Edit3,
  Save,
  X
} from 'lucide-react';
import { Verification } from '../../../types/verification';
import { useLanguage } from '../../../contexts/LanguageContext';
import { uploadFile, deleteFile } from '../../../utils/firebase';
import toast from 'react-hot-toast';

type Props = {
  verification: Verification;
  onClose: () => void;
  onContractSigned: (signedContractUrl: string) => void;
};

export const AdminContractHandler = ({
  verification,
  onClose,
  onContractSigned
}: Props) => {
  const { translations } = useLanguage();
  const [isSigning, setIsSigning] = useState(false);
  const [signedContract, setSignedContract] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [tempSignedContractUrl, setTempSignedContractUrl] = useState<string | null>(null);

  const handleDownloadContract = () => {
    if (!verification.contractUrl) return;
    
    const link = document.createElement('a');
    link.href = verification.contractUrl;
    link.target = '_blank';
    link.download = `contract-${verification.userName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSignContract = () => {
    setIsSigning(true);
  };

  const handleSaveSignature = async () => {
    if (!signedContract) return;
    
    setIsUploading(true);
    try {
      // Delete the old signed contract if it exists
      if (verification.signedContractUrl) {
        try {
          await deleteFile(verification.signedContractUrl);
        } catch (error) {
          console.warn('Failed to delete old signed contract:', error);
        }
      }
      
      const result = await uploadFile(signedContract, `signed-contract-${verification.id}.pdf`);
      setTempSignedContractUrl(result.url);
      onContractSigned(result.url);
      toast.success('Contract signed and uploaded successfully');
    } catch (error) {
      console.error('Error uploading signed contract:', error);
      toast.error('Failed to upload signed contract');
    } finally {
      setIsUploading(false);
      setIsSigning(false);
    }
  };

  const handleCancelSigning = () => {
    setIsSigning(false);
    setSignedContract(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">
              {'Contract Handling'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-6">
            {/* Contract Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">
                {'Contract Information'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    {'User'}:
                  </p>
                  <p className="font-medium">{verification.userName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {'User Type'}:
                  </p>
                  <p className="font-medium capitalize">{verification.userType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {'Created'}:
                  </p>
                  <p className="font-medium">
                    {new Date(verification.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Contract Actions */}
            {!isSigning ? (
              <div className="space-y-4">
                {/* Download Original Contract */}
                {verification.contractUrl && (
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <FileText className="text-primary-500" size={20} />
                      {'Original Contract'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {'Download the contract sent by the organizer/guide'}
                    </p>
                    <button
                      onClick={handleDownloadContract}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      <Download size={18} />
                      <span>
                        {'Download Contract'}
                      </span>
                    </button>
                  </div>
                )}

                {/* View Original Contract */}
                {verification.contractUrl && (
                  <div className="bg-white border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">
                      {'View Original Contract'}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {'View the contract in your browser'}
                    </p>
                    <a
                      href={verification.contractUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-10 transition-colors"
                    >
                      <ExternalLink size={18} />
                      <span>
                        {'View Contract'}
                      </span>
                    </a>
                  </div>
                )}

                {/* Sign Contract */}
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Edit3 className="text-primary-500" size={20} />
                    {'Sign Contract'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {'Download the contract, sign it, and upload the signed version. This step is required before approving the verification.'}
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 0-1 1v3a1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          <strong>Required Step:</strong> You must sign and upload the contract before you can approve this verification.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSignContract}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <Edit3 size={18} />
                    <span>
                      {'Sign Contract'}
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              /* Signing Interface */
              <div className="space-y-4">
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">
                    {'Upload Signed Contract'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {'Upload the signed contract after downloading and signing it'}
                  </p>
                  
                  {tempSignedContractUrl ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                      <CheckCircle size={20} />
                      <span>
                        {'Contract signed and uploaded successfully'}
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer border-gray-300 hover:border-primary-300 bg-gray-50 hover:bg-gray-100">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 mb-4 text-gray-500" />
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">
                                {'Click to upload'}
                              </span>{' '}
                              {'or drag and drop'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {'PDF only (MAX. 10MB)'}
                            </p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                setSignedContract(e.target.files[0]);
                              }
                            }}
                          />
                        </label>
                      </div>
                      
                      {signedContract && (
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="text-blue-500" size={20} />
                            <span className="text-sm font-medium">{signedContract.name}</span>
                          </div>
                          <button
                            onClick={() => setSignedContract(null)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <button
                          onClick={handleCancelSigning}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {'Cancel'}
                        </button>
                        
                        <button
                          onClick={handleSaveSignature}
                          disabled={!signedContract || isUploading}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${
                            !signedContract || isUploading
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-green-500 hover:bg-green-600'
                          }`}
                        >
                          {isUploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>
                                {'Uploading...'}
                              </span>
                            </>
                          ) : (
                            <>
                              <Save size={18} />
                              <span>
                                {'Save Signature'}
                              </span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* View Signed Contract */}
            {verification.signedContractUrl && (
              <div className="bg-white border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <FileText className="text-green-500" size={20} />
                  {'View Signed Contract'}
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  {'View the signed contract that has been uploaded'}
                </p>
                <a
                  href={verification.signedContractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <ExternalLink size={18} />
                  <span>
                    {'View Signed Contract'}
                  </span>
                </a>
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              {'Close'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};